import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Lock } from 'lucide-react';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionEstablished, setSessionEstablished] = useState(false);
  const [tokens, setTokens] = useState<{ accessToken: string; refreshToken: string } | null>(null);

  useEffect(() => {
    // Don't run validation if session is already established
    if (sessionEstablished) {
      return;
    }

    // Parse parameters from both query string and hash fragment
    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    // Check for Supabase errors first
    const error = hashParams.get('error');
    const errorCode = hashParams.get('error_code');
    const errorDescription = hashParams.get('error_description');
    
    if (error) {
      console.log('Supabase error detected:', { error, errorCode, errorDescription });
      
      let errorMessage = "Password reset link error. ";
      
      if (errorCode === 'otp_expired') {
        errorMessage += "This password reset link has expired. Please request a new one.";
      } else if (errorCode === 'access_denied') {
        errorMessage += "Access denied. The link may be invalid or expired.";
      } else {
        errorMessage += errorDescription || "Please request a new password reset link.";
      }
      
      toast({
        title: "Link Expired",
        description: errorMessage,
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    
    // Check query string first, then hash fragment
    const type = queryParams.get('type') || hashParams.get('type');
    const accessToken = queryParams.get('access_token') || hashParams.get('access_token');
    const refreshToken = queryParams.get('refresh_token') || hashParams.get('refresh_token');
    
    // Debug: Log all URL parameters
    console.log('Reset password URL parameters:');
    console.log('Type:', type);
    console.log('Access Token:', accessToken ? 'Present' : 'Missing');
    console.log('Refresh Token:', refreshToken ? 'Present' : 'Missing');
    console.log('Full URL:', window.location.href);
    console.log('Search params:', window.location.search);
    console.log('Hash params:', window.location.hash);
    
    // For password reset, we need type=recovery and the tokens
    if (type !== 'recovery' || !accessToken || !refreshToken) {
      console.log('Invalid reset link - missing required parameters');
      console.log('Expected: type=recovery, access_token, refresh_token');
      console.log('Found: type=' + type + ', access_token=' + (accessToken ? 'present' : 'missing') + ', refresh_token=' + (refreshToken ? 'present' : 'missing'));
      toast({
        title: "Invalid reset link",
        description: "This password reset link is invalid or has expired. Please request a new one.",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    // Store the tokens for later use, but don't set the session yet
    // This prevents automatic redirect to dashboard
    console.log('Valid reset link detected - storing tokens for password reset');
    setSessionEstablished(true);
    
    // Store tokens in component state for use during password reset
    setTokens({ accessToken, refreshToken });
    
    // Show success message that they can now reset their password
    toast({
      title: "Reset link valid",
      description: "You can now set your new password below",
    });
  }, [searchParams, navigate, toast, sessionEstablished]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Password reset form submitted');
    
    if (isLoading) {
      console.log('Already processing, ignoring duplicate submission');
      return;
    }
    
    if (!tokens) {
      toast({
        title: "Session expired",
        description: "Please request a new password reset link",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive"
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // First, set the session with the stored tokens
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken
      });

      if (sessionError) {
        throw new Error('Session expired. Please request a new password reset link.');
      }

      // Then update the password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Password updated successfully",
        description: "Your password has been reset. You can now log in with your new password."
      });

      console.log('Password updated successfully, logging out and redirecting...');
      
      // Logout the user and redirect to login
      await supabase.auth.signOut();
      
      console.log('Logged out, navigating to login page...');
      
      // Add a small delay to ensure the toast is shown
      setTimeout(() => {
        console.log('Attempting navigation to login...');
        try {
          navigate('/login');
        } catch (navError) {
          console.error('Navigation error:', navError);
          // Fallback to direct URL change
          window.location.href = '/login';
        }
      }, 1000);
    } catch (error: any) {
      toast({
        title: "Error updating password",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-8 p-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-3xl font-bold">Reset Password</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your new password below
          </p>
        </div>

        <form onSubmit={handleResetPassword} className="mt-8 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Updating Password...' : 'Update Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
